/**
 * Hook for managing Kanban board phase operations
 * @remarks
 * Extracted to reduce cognitive complexity.
 * Provides handler functions for phase CRUD operations.
 */
import { useCallback } from "react";

import type useKanbanBoardMutations from "./useKanbanBoardMutations";

import type { KanbanBoard, KanbanCard, KanbanPhase } from "@/generated/graphql";

import { nextAppendedStagePosition } from "../utils/stageEditorOps";

/**
 * Dependencies for the phase operations hook
 */
interface PhaseOperationsDeps {
  /** Current kanban board */
  board: KanbanBoard | null;
  /** Current phases */
  phases: KanbanPhase[];
  /** Current cards */
  cards: KanbanCard[];
  /** Mutation functions */
  mutations: ReturnType<typeof useKanbanBoardMutations>;
  /** Refetch phases function */
  refetchPhases: () => Promise<unknown>;
  /** Error setter */
  setError: (error: string | null) => void;
  /** Saving state setter */
  setIsSaving: (saving: boolean) => void;
}

/**
 * Return type for the useKanbanPhaseOperations hook
 */
interface PhaseOperationsResult {
  /** Update board name */
  updateBoardName: (newName: string) => Promise<void>;
  /** Update phase name */
  updatePhaseName: (phaseId: string, newName: string) => Promise<void>;
  /** Add new phase */
  addPhase: (name: string) => Promise<void>;
  /** Delete phase */
  deletePhase: (phaseId: string) => Promise<boolean>;
  /** Update phase positions */
  updatePhasePositions: (
    boardId: string,
    positions: { phaseId: string; position: number }[]
  ) => Promise<void>;
}

/**
 * Handle error from delete phase operation
 * @param err - The caught error
 * @param setError - Error setter function
 * @returns void - Sets error message via callback, does not return a value
 */
const handleDeletePhaseError = (
  err: unknown,
  setError: (error: string) => void
): void => {
  const errorMessage = err instanceof Error ? err.message : "Unknown error";
  const isConstraintViolation =
    err instanceof Error &&
    err.message.includes("duplicate key value violates unique constraint");

  const errorMsg = isConstraintViolation
    ? "Unable to delete column due to a database constraint. Please try again."
    : `Failed to delete column: ${errorMessage}`;

  setError(errorMsg);
};

/**
 * Calculate new phase positions after deletion
 *
 * SE-6535 — collision safety here rests on ONE load-bearing property: every
 * assigned position starts strictly ABOVE the board's current maximum, so no
 * value the payload writes can be one another row still holds. The backend
 * applies these updates sequentially in a single transaction under a
 * non-deferred unique `(kanbanBoardId, position)` constraint, so any scheme that
 * re-issues a live position raises 23505 mid-transaction and rolls the whole
 * operation back. Do NOT change `startPosition` to compact or renumber from
 * zero; if this ever needs a different origin, route it through
 * `utils/phasePositions.ts` (which guarantees disjointness explicitly) instead.
 * @param phases - Current phases
 * @param phaseIdToDelete - ID of phase being deleted
 * @returns Array of phase position objects with IDs and new positions, excluding the deleted phase
 */
const calculatePhasePositionsAfterDelete = (
  phases: KanbanPhase[],
  phaseIdToDelete: string
): { phaseId: string; position: number }[] => {
  const remainingPhases = phases.filter(p => p.id !== phaseIdToDelete);
  const maxPosition = Math.max(...phases.map(p => p.position));
  const GAP_SIZE = 100000;
  const startPosition = maxPosition + GAP_SIZE;

  return remainingPhases.map((phase, index) => ({
    phaseId: phase.id,
    position: startPosition + index * GAP_SIZE,
  }));
};

/**
 * Hook for kanban phase operations
 * @param deps - Dependencies for operations
 * @returns Object with functions for updating board/phase names, adding, deleting, and reordering phases
 */
const useKanbanPhaseOperations = (
  deps: PhaseOperationsDeps
): PhaseOperationsResult => {
  const {
    board,
    phases,
    cards,
    mutations,
    refetchPhases,
    setError,
    setIsSaving,
  } = deps;

  const updateBoardName = useCallback(
    async (newName: string) => {
      if (!board) return;
      setIsSaving(true);
      try {
        await mutations.updateBoardMutation({
          variables: { id: board.id, input: { name: newName } },
        });
      } finally {
        setIsSaving(false);
      }
    },
    [board, mutations, setIsSaving]
  );

  const updatePhaseName = useCallback(
    async (phaseId: string, newName: string) => {
      setIsSaving(true);
      try {
        await mutations.updatePhaseMutation({
          variables: { id: phaseId, input: { name: newName } },
        });
      } finally {
        setIsSaving(false);
      }
    },
    [mutations, setIsSaving]
  );

  /**
   * SE-6558 — the new column must be APPENDED, so its position is anchored
   * strictly above the board's current maximum via the feature's single append
   * scheme (`nextAppendedStagePosition`, shared with `useStageEditor.addStage`).
   * This previously passed `phases.length`, a small integer far below the band
   * live phases occupy (100000/200000/300000, or `[1M, 1B)` once a board has
   * been reordered through `utils/phasePositions`), so columns rendered in
   * ascending position order put every new column FIRST instead of last.
   */
  const addPhase = useCallback(
    async (name: string) => {
      if (!board) return;
      setIsSaving(true);
      try {
        await mutations.createPhaseMutation({
          variables: {
            input: {
              kanbanBoardId: board.id,
              name,
              position: nextAppendedStagePosition(
                phases.map(phase => phase.position)
              ),
            },
          },
        });
      } finally {
        setIsSaving(false);
      }
    },
    [board, phases, mutations, setIsSaving]
  );

  const deletePhase = useCallback(
    async (phaseId: string): Promise<boolean> => {
      const phaseCards = cards.filter(c => c.kanbanPhase.id === phaseId);
      if (phaseCards.length > 0) {
        setError("Cannot delete column with players. Move players first.");
        return false;
      }
      if (!board?.id) {
        setError("Board ID not available");
        return false;
      }

      setIsSaving(true);
      try {
        const phasePositions = calculatePhasePositionsAfterDelete(
          phases,
          phaseId
        );
        await mutations.updatePhasePositionsMutation({
          variables: { input: { kanbanBoardId: board.id, phasePositions } },
        });
        await mutations.deletePhaseMutation({ variables: { id: phaseId } });
        await refetchPhases();
        return true;
      } catch (err) {
        handleDeletePhaseError(err, setError);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [cards, board, phases, mutations, refetchPhases, setError, setIsSaving]
  );

  const updatePhasePositions = useCallback(
    async (
      targetBoardId: string,
      phasePositions: { phaseId: string; position: number }[]
    ) => {
      try {
        await mutations.updatePhasePositionsMutation({
          variables: {
            input: { kanbanBoardId: targetBoardId, phasePositions },
          },
        });
      } catch (_err) {
        // Error handled by onError callback
      }
    },
    [mutations]
  );

  return {
    updateBoardName,
    updatePhaseName,
    addPhase,
    deletePhase,
    updatePhasePositions,
  };
};

export default useKanbanPhaseOperations;
